// Packages
import {
  BrowserRouter,
  Link,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
// Contexts
import { AppProviders } from "./_common/components/AppProviders.tsx";

// Pages
import { NavBar } from "./_common/components";
import AppSettings from "./_common/components/AppSettings.tsx";
import { FirstSignIn, ProtectedRoute } from "./auth/components";
import RouteTracker from "./_common/components/RouteTracker.jsx";

// Dialogs
import { InstructionsDialog } from "./_common/components/dialogs";

// UI Components
import { Button, Spinner } from "./_common/components/ui";

// Error Handling
import ErrorBoundary from "@jkast90/kast-common/error-boundary";
import {
  AppShell,
  LinkProvider,
  SiteFooter,
  TooltipHost,
  buttonClass,
  useKeyboardShortcuts,
} from "@jkast90/kast-common/ui";

// Styling
import "./App.css";

// Utils
import { logger } from "./_common/utils/logger.ts";
import {
  installGlobalErrorHandlers,
  logger as diagnostics,
} from "@jkast90/kast-common/diagnostics";

// Each page is its own chunk, fetched on first visit. A failed fetch (say, a
// chunk pruned by a later deploy) throws into the ErrorBoundary below.
const About = lazy(() => import("./about/pages/About"));
const Browse = lazy(() => import("./browse/pages/Browse"));
const Feed = lazy(() => import("./feed/pages/Feed"));
const Account = lazy(() => import("./account/pages/Account"));
const Profile = lazy(() => import("./profile/pages/Profile"));
const Wishlist = lazy(() => import("./wishlist/pages/Wishlist"));
const Memories = lazy(() => import("./memories/pages/Memories"));
const Imprints = lazy(() => import("./imprints/pages/Imprints"));
const Families = lazy(() => import("./families/pages/Families"));
const Calendar = lazy(() => import("./calendar/pages/Calendar"));
const Events = lazy(() => import("./calendar/pages/Events"));
const YearlySummary = lazy(
  () => import("./yearly-summary/pages/YearlySummary"),
);
const Admin = lazy(() => import("./admin/pages/Admin"));
const BugReport = lazy(() => import("./bug-reports/pages/BugReport"));

function AppWithPrompt() {
  const location = useLocation();
  const navigate = useNavigate();

  const [showInstructions, setShowInstructions] = useState(() => {
    const hasSeenInstructions = localStorage.getItem("hasSeenInstructions");
    return hasSeenInstructions !== "true";
  });
  const [tourMode, setTourMode] = useState(false);

  // Uncaught errors and unhandled rejections go to the diagnostics logger,
  // so they reach a bug report (installed once, whatever StrictMode does).
  useEffect(() => installGlobalErrorHandlers(), []);

  // "?" opens the instructions (Shift+/ on most keyboards), unless the user
  // is typing.
  useKeyboardShortcuts({ "?": () => setShowInstructions(true) });

  // Registered in production builds only; in dev a worker left over from an
  // earlier run is removed so the dev server's files are never served from it.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    if (import.meta.env.PROD) {
      navigator.serviceWorker
        .register("/service-worker.js")
        .catch((err) =>
          logger.warn("Service worker registration failed:", err),
        );
    } else {
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) =>
          registrations.forEach((registration) => registration.unregister()),
        )
        .catch(() => undefined);
    }
  }, []);

  return (
    <>
      {/* Every element with data-tip gets a tooltip. */}
      <TooltipHost />
      <RouteTracker />
      <FirstSignIn />
      {/* The boundary sits below the NavBar, so it survives a crash, and is
          keyed on the route's section (first path segment), so moving to
          another section clears it without remounting on query or id changes. */}
      <AppShell
        fixedHeader
        header={<NavBar onHelpClick={() => setShowInstructions(true)} />}
        footer={<SiteFooter collapsible={false} />}
      >
        <ErrorBoundary
          key={location.pathname.split("/")[1]}
          onError={(error, info) =>
            diagnostics.error(
              `[Crash] ${error.message}`,
              info.componentStack ?? "",
            )
          }
          onReport={(crashNote) =>
            navigate("/bug-report", { state: { crashNote } })
          }
          buttonClassName={buttonClass()}
          primaryButtonClassName={buttonClass({ variant: "primary" })}
        >
          <Suspense
            fallback={
              <div className="routeFallback">
                <Spinner size="lg" label="Loading page…" />
              </div>
            }
          >
            <Routes>
              {/* The old hosted-UI callback; sign-in now returns to the page it started from. */}
              <Route path="/auth" element={<Navigate to="/" replace />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <About />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/browse"
                element={
                  <ProtectedRoute>
                    <Browse />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/about"
                element={
                  <ProtectedRoute>
                    <About />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/feed"
                element={
                  <ProtectedRoute>
                    <Feed />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/wishlist"
                element={
                  <ProtectedRoute>
                    <Wishlist />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/memories"
                element={
                  <ProtectedRoute>
                    <Memories />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/imprints"
                element={
                  <ProtectedRoute>
                    <Imprints />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/families"
                element={
                  <ProtectedRoute>
                    <Families />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <Calendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/events"
                element={
                  <ProtectedRoute>
                    <Events />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/yearly-summary"
                element={
                  <ProtectedRoute>
                    <YearlySummary />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/account"
                element={
                  <ProtectedRoute>
                    <Account />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requireGroup="admin">
                    <Admin />
                  </ProtectedRoute>
                }
              />
              <Route path="/bug-report" element={<BugReport />} />
              <Route
                path="*"
                element={
                  <div
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      color: "var(--text)",
                      backgroundColor: "var(--surface)",
                      margin: "2rem",
                      borderRadius: "8px",
                      border: "1px solid var(--accent-dark)",
                      boxShadow: "0 2px 8px var(--shadow-color)",
                    }}
                  >
                    <h1 style={{ color: "var(--text)", marginBottom: "1rem" }}>
                      404 – Page Not Found
                    </h1>
                    <p
                      style={{
                        color: "var(--text-muted)",
                        marginBottom: "1.5rem",
                      }}
                    >
                      The page you're looking for doesn't exist.
                    </p>
                    <Button
                      onClick={() => window.history.back()}
                      style={{ marginRight: "0.5rem" }}
                    >
                      Go Back
                    </Button>
                    <a
                      href="/"
                      style={{
                        padding: "0.5rem 1rem",
                        backgroundColor: "var(--surface)",
                        color: "var(--text)",
                        border: "1px solid var(--accent-dark)",
                        borderRadius: "4px",
                        textDecoration: "none",
                        display: "inline-block",
                      }}
                    >
                      Home
                    </a>
                  </div>
                }
              />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AppShell>
      <AppSettings />
      <InstructionsDialog
        open={showInstructions}
        onClose={() => {
          setShowInstructions(false);
          setTourMode(false);
          localStorage.setItem("hasSeenInstructions", "true");
        }}
        tourMode={tourMode}
        onStartTour={() => {
          setTourMode(true);
        }}
      />
    </>
  );
}

export function AppContent() {
  return <AppWithPrompt />;
}

export default function App() {
  return (
    <AppProviders>
      <BrowserRouter>
        <LinkProvider link={Link}>
          <AppContent />
        </LinkProvider>
      </BrowserRouter>
    </AppProviders>
  );
}
